import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchEventById } from "../../store/eventsSlice";
import { createTicket } from "../../store/ticketsSlice";
import GuestTicketForm from "../../components/GuestTicketForm";

export default function EventDetailsPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { selectedEvent, loading, error } = useSelector(
    (state) => state.events
  );
  const { error: ticketError } = useSelector((state) => state.tickets);
  const [toastVisible, setToastVisible] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);

  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    min: 0,
    sec: 0,
  });

  // Fetch event when component mounts or id changes
  useEffect(() => {
    if (id) {
      dispatch(fetchEventById(id));
    }
  }, [id, dispatch]);

  // Countdown timer
  useEffect(() => {
    if (!selectedEvent?.date) return;

    const calculateCountdown = () => {
      const eventDate = new Date(selectedEvent.date).getTime();
      const now = new Date().getTime();
      const distance = eventDate - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setCountdown({ days, hours, min: minutes, sec: seconds });
      } else {
        clearInterval(timer);
      }
    };

    const timer = setInterval(calculateCountdown, 1000);
    calculateCountdown(); // Run immediately

    return () => clearInterval(timer);
  }, [selectedEvent?.date]);

  // Function to format date and time
  const formatDateTime = (isoDate) => {
    const eventDate = new Date(isoDate);
    const date = eventDate.toLocaleDateString("fr-FR");
    const time = eventDate.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return { date, time };
  };

  // Handle Get Tickets button click
  const handleGetTickets = () => {
    if (!selectedEvent) return;

    setTicketLoading(true);

    const ticketData = {
      eventId: selectedEvent._id,
      title: selectedEvent.title,
      date: selectedEvent.date,
      location: selectedEvent.location,
    };

    dispatch(createTicket(ticketData))
      .unwrap()
      .then((response) => {
        console.log("Ticket created successfully:", response);
        setToastVisible(true);
        setTimeout(() => {
          setTimeout(() => setToastVisible(false), 500);
        }, 5000);
      })
      .catch((error) => {
        console.error("Error creating ticket:", error);
        setToastVisible(true);
      })
      .finally(() => {
        setTicketLoading(false);
      });
  };

  // Show error toast when there's an error
  useEffect(() => {
    if (ticketError) {
      setToastVisible(true);
      setTimeout(() => {
        setTimeout(() => setToastVisible(false), 500);
      }, 5000);
    }
  }, [ticketError]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !selectedEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">
            {error || "Event not found"}
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  const { date: formattedDate, time } = formatDateTime(selectedEvent.date);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-8">
            <h1 className="text-4xl font-bold mb-8">{selectedEvent.title}</h1>

            <div className="flex flex-col md:flex-row gap-8">
              {/* Left side - Image and Description */}
              <div className="md:w-2/3">
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="relative w-full mb-6 rounded-lg overflow-hidden">
                    <img
                      src={
                        selectedEvent.image
                          ? selectedEvent.image.url || selectedEvent.image
                          : "https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                      }
                      alt={selectedEvent.title}
                      className="w-full h-96 object-cover"
                    />
                  </div>

                  <p className="mb-4 text-gray-700 leading-relaxed">
                    {selectedEvent.description}
                  </p>
                  <p className="mb-2 text-gray-600">
                    📅 Date : {formattedDate}
                  </p>
                  <p className="mb-2 text-gray-600">🕒 Time : {time}</p>
                  <p className="font-semibold text-red-600">
                    ⚡ Limited places - Book now for an exceptional experience!
                  </p>
                </div>
              </div>

              {/* Right side - Event Details */}
              <div className="md:w-1/3 flex flex-col gap-4">
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <h2 className="text-xl font-semibold mb-4">Event Details</h2>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {Object.entries(countdown).map(([unit, value]) => (
                      <div
                        key={unit}
                        className="bg-red-500 text-white p-2 rounded text-center"
                      >
                        <div className="text-2xl font-bold">{value}</div>
                        <div className="text-xs uppercase">{unit}</div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-xs">
                        🕒
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">
                          Date and Time
                        </div>
                        <div>
                          {formattedDate} at {time}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-xs">
                        📍
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Location</div>
                        <div>{selectedEvent.location}</div>
                      </div>
                    </div>
                  </div>
                </div>


                {/* Get Tickets Section */}
                {!user ? (
                  // Show Guest Ticket Form for unauthenticated users
                  <GuestTicketForm
                    eventId={selectedEvent._id}
                    eventTitle={selectedEvent.title}
                    onSuccess={(response) => {
                      console.log("Guest ticket created:", response);
                    }}
                  />
                ) : user.role === "user" ? (
                  // Show existing authenticated ticket flow for regular users
                  <div>
                    <button
                      onClick={handleGetTickets}
                      className={`w-full ${ticketLoading ? "bg-gray-400" : "bg-red-500"
                        } text-white py-4 rounded-md hover:bg-red-600 transition duration-300 my-5`}
                      disabled={ticketLoading}
                    >
                      {ticketLoading ? "Loading..." : "Get your ticket"}
                    </button>
                  </div>
                ) : (
                  // Show sign-in link for admin or other roles
                  <Link
                    to="/login"
                    className="w-full bg-gray-500 text-white p-3 mb-5 text-center rounded-md hover:bg-gray-600 transition duration-300 block"
                  >
                    Sign in to get your ticket
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast/Modal for Loading, Success, or Error */}
      {(ticketLoading || toastVisible || ticketError) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center animate-fadeIn">
            <div className="flex flex-col items-center space-y-4">
              {ticketLoading ? (
                <>
                  <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                  <p className="text-lg font-semibold text-gray-700">
                    Processing your ticket...
                  </p>
                  <p className="text-sm text-gray-500">
                    This might take a few seconds, please wait.
                  </p>
                </>
              ) : ticketError ? (
                <>
                  <svg
                    className="w-12 h-12 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m6 4h.01M6 18h.01M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
                    />
                  </svg>
                  <p className="text-lg font-semibold text-gray-700">
                    Failed to send ticket!
                  </p>
                  <p className="text-sm text-gray-500">
                    {ticketError}. Please try again.
                  </p>
                  <button
                    onClick={() => setToastVisible(false)}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <svg
                    className="w-12 h-12 text-green-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <p className="text-lg font-semibold text-gray-700">
                    Ticket sent successfully!
                  </p>
                  <p className="text-sm text-gray-500">
                    Please check your email for the details.
                  </p>
                  <button
                    onClick={() => setToastVisible(false)}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
